"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import BreakPointData from "@/data/BreakPointData";
import styles from "./courses.module.scss";
import Components from "@/components";
import sk from "@/skeletons";
import api from "@/api";

function AllCourses() {
  const [breakPointIndex, setBreakPointIndex] = useState(1);
  const [windowWidth, setWindowWidth] = useState(0);
  const [grpCourses, setGrpCourses] = useState([]);
  const [allCourse, setAllCourse] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const page = useRef(1);

  // Define breakpoints and items per page
  const getGroupSizeAndApiLimit = (width) => {
    if (width <= 481) return { groupSize: 2, limit: 8 };
    if (width <= 1080) return { groupSize: 3, limit: 12 };
    if (width <= 1500) return { groupSize: 4, limit: 12 };
    if (width <= 2180) return { groupSize: 5, limit: 15 };
    if (width <= 2559) return { groupSize: 6, limit: 18 };
    return { groupSize: 7, limit: 21 };
  };

  // Handle window resize and set windowWidth
  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => setWindowWidth(window.innerWidth);
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const groupCourses = (courses) => {
    const { groupSize } = getGroupSizeAndApiLimit(windowWidth);
    const grouped = [];
    for (let i = 0; i < courses.length; i += groupSize) {
      grouped.push(courses.slice(i, i + groupSize));
    }
    return grouped;
  };

  const fetchCourses = useCallback(async () => {
    if (windowWidth === 0) return; // Wait for windowWidth to initialize

    const { limit } = getGroupSizeAndApiLimit(windowWidth);
    const res = await api.getallcourse(page.current, limit);

    if (res.status === 200) {
      const newCourses = res.data?.result?.data;
      setAllCourse((prev) => [...prev, ...newCourses]);
      setGrpCourses((prev) => {
        const updatedCourses = [...prev, ...groupCourses(newCourses)];
        if (breakPointIndex < BreakPointData.length) {
          updatedCourses.push(BreakPointData[breakPointIndex]);
        }
        return updatedCourses;
      });

      if (breakPointIndex < BreakPointData.length) {
        setBreakPointIndex((prev) => prev + 1);
      }

      if (res.data.result.next?.page > page.current) {
        page.current += 1;
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    }
  }, [windowWidth, breakPointIndex]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleLoadMore = async (type) => {
    if (type === "all courses" && hasMore) {
      let res = {};
      const coursesPerPage =
        windowWidth <= 1600 && windowWidth >= 481
          ? 12
          : windowWidth <= 2180 && windowWidth >= 1601
            ? 15
            : windowWidth >= 2181 && windowWidth < 2559
              ? 18
              : windowWidth >= 2559
                ? 21
                : 10; // Default to 10 courses

      try {
        res = await api.getallcourse(page.current, coursesPerPage);

        if (res.status === 200) {
          const newCourses = res.data.result.data;

          setAllCourse((prev) => [...prev, ...newCourses]);
          setGrpCourses((prev) => {
            const updatedCourses = [...prev, ...groupCourses(newCourses)];
            if (breakPointIndex < BreakPointData.length) {
              updatedCourses.push(BreakPointData[breakPointIndex]);
            }
            return updatedCourses;
          });

          if (res.data.result.next?.page > page.current) {
            page.current += 1;
            setHasMore(true);
          } else {
            setHasMore(false); // No more pages to load
          }
        }
      } catch (error) {
        console.error("Error loading more courses:", error);
        setHasMore(false); // Prevent infinite attempts if an error occurs
      }
    }
  };

  return (
    <section className={styles["hm-category-rows"]} id="target">
      <header className={styles["title"]}>
        <h1>All Courses</h1>
      </header>
      <InfiniteScroll
        hasMore={hasMore}
        scrollableTarget={"target"}
        dataLength={allCourse?.length}
        next={() => handleLoadMore("all courses")}
        loader={
          <div
            className={styles["hm-category-videos2"]}
            style={{ padding: "0px" }}
          >
            <sk.RowCardSkeleton number={8} />
          </div>
        }
        className={styles["hm-category-videos2"]}
        style={{ gridTemplateColumns: "repeat(1, 1fr)" }}
      >
        {grpCourses.map((courseGroup, i) => (
          <React.Fragment key={i}>
            {!Array.isArray(courseGroup) ? (
              <div
                className={styles["hm-category-videos2"]}
                style={{
                  gridTemplateColumns: "repeat(1, 1fr)",
                  padding: "0px",
                }}
              >
                <Components.BreakPoint
                  heading={courseGroup?.heading}
                  subHeading={courseGroup?.paraHeading}
                  src={courseGroup?.src}
                  backgroundColor={courseGroup?.backgroundColor}
                  navigationLink={courseGroup?.navigationLink}
                />
              </div>
            ) : (
              <div
                className={styles["hm-category-videos2"]}
                style={{ padding: "0px" }}
              >
                {courseGroup.map(
                  (course, idx) =>
                    course && (
                      <Components.CourseCard
                        key={course?._id || idx}
                        type="course"
                        id={course?._id}
                        seoTitle={course?.seoTitle}
                        videoTitle={course?.firstVideoTitle}
                        visibility={course?.visibility}
                        image={course?.thumbnailImage}
                        tag={course?.categories}
                        enrollCount={course?.enrolled_count}
                        title={course?.title}
                        videoCount={course?.videosCount}
                        timeCount={course?.totalTime}
                        creatorInfo={{
                          id: course?.creatorInfo?.[0]?._id,
                          name: course?.creatorInfo?.[0]?.name,
                          username: course?.creatorInfo?.[0]?.username,
                          image: course?.creatorInfo?.[0]?.profileImage,
                        }}
                      />
                    )
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </InfiniteScroll>
    </section>
  );
}

export default React.memo(AllCourses);
